import { createFileRoute, redirect } from '@tanstack/react-router'
import type { AxiosError } from 'axios'

import { sessionStore } from '@gto/session'

import { accountOauthGtowizardCreate } from '@/api/gen'
import { Loader } from '@/components/loader'
import { OAuthCodeCheckRoute } from '@/pages/auth/route-entries/oath-code-check'
import { defaultOAuthQuery, oAuthSchema } from '@/pages/auth/search-schema'
import { store } from '@/store'
import { getAxiosErrorMessage, getBackUrl } from '@/utils/helpers'

export const Route = createFileRoute('/_auth/login/oauth/')({
  pendingComponent: Loader,
  component: OAuthCodeCheckRoute,
  validateSearch: (search) => oAuthSchema.catch(defaultOAuthQuery).parse(search),
  loaderDeps: ({ search }) => {
    return {
      search,
    }
  },
  loader: async ({ deps: { search } }) => {
    let status: 'success' | 'error' = 'success'
    let message = ''
    const state = store.getState()

    if (!state.isInitialized) {
      await state.initialize()
    }

    if (search.code === undefined) {
      status = 'error'

      return { status, message }
    }

    // This loader can be invoked multiple times during a single login flow,
    // especially if state changes cause a re-render. To prevent sending the
    // single-use OAuth 'code' to the backend more than once, we use sessionStorage
    // to track if a code has already been processed. An in-memory flag would be
    // lost during the page redirect inherent in the OAuth process.
    const processedCodeKey = `oauth_processed_${search.code}`
    if (sessionStorage.getItem(processedCodeKey) !== null) {
      // Code already processed, return without making another request
      return { status, message }
    }

    // Mark this code as being processed
    sessionStorage.setItem(processedCodeKey, 'true')

    try {
      // todo: validate oAuth code with RBwizard BE
      const result = await accountOauthGtowizardCreate({
        code: search.code,
        redirect_uri: window.location.origin + '/login/oauth',
      })

      status = 'success'
      localStorage.setItem('user_refresh', result.refresh)
      sessionStore.getState().setAccessToken(result.access)

      if (result.is_new_user) {
        sessionStorage.setItem('new_user_flow', 'true')

        const termsAccepted = result.user.term_and_condition === true
        store.setState({
          user: result.user,
          isAuthorized: true,
          emailValidated: result.user.email_verified,
          isInitialized: true,
          termsAccepted,
        })

        throw redirect({
          to: '/login/oauth/start',
          search: {
            ...search,
            code: undefined,
          },
        })
      }

      state.initialize().catch(console.error)

      if (store.getState().isAuthorized) {
        throw redirect({
          to: getBackUrl(search.back_url),
          replace: true,
        })
      }
    } catch (error) {
      if (error instanceof Response) {
        throw error
      }

      message = getAxiosErrorMessage(error as AxiosError)
      status = 'error'
      console.error('Error validating oAuth code:', error)
    }

    return {
      status,
      message,
    }
  },
})
